import React, { useContext } from "react";
import { Box, Typography } from "@mui/material";
import { type BulkPropertyRow } from "../types";
import { useCheckedVersion } from "../hooks/useCheckedIdsStore";
import { CHECKBOX_FIELD_NAMES } from "../utils/checkboxFields";
import {
  summarizeRowSources,
  type SourceKind,
} from "../utils/rowSourceSummary";
import { BulkCheckboxCellContext } from "./BulkCheckboxCell";
import { BULK_MARK_COLORS } from "./bulkMarkColors";

const SOURCE_COLOR: Partial<Record<SourceKind, string>> = {
  new: BULK_MARK_COLORS.unacquiredLine,
  reuse: BULK_MARK_COLORS.reusableLine,
};

/** 取得方法セル1行分の高さ（px）。グリッドの行高計算と描画で同じ値を使う */
export const SOURCE_LINE_HEIGHT = 20;

/**
 * その行のチェック済み書類が、今の取得条件で送信時にどう振り分けられるかを常時見せる。
 * 列ごとの印は候補の有無しか示せず、全部事項は候補と違う組み合わせをチェックすると
 * 新規取得（通常料金）に落ちることが見えないため、送信と同じ判定で行単位の結果を出す
 */
export const BulkRowSourceCell: React.FC<{ row: BulkPropertyRow }> = React.memo(
  ({ row }) => {
    const context = useContext(BulkCheckboxCellContext);
    if (context == null) {
      throw new Error(
        "BulkRowSourceCell は BulkCheckboxCellProvider の内側で使う"
      );
    }
    const { store, policy, pastDays, now } = context;
    // 9列すべてのチェックを見るため、列単位ではなく版数で購読する
    useCheckedVersion(store);
    const lines = summarizeRowSources(
      row,
      store.getRowFields(CHECKBOX_FIELD_NAMES, row.id),
      policy,
      pastDays,
      now
    );
    if (lines.length === 0) {
      return null;
    }
    return (
      <Box sx={{ py: 0.5 }}>
        {lines.map((line) => (
          <Typography
            key={line.text}
            variant="body2"
            noWrap
            sx={{
              lineHeight: `${SOURCE_LINE_HEIGHT}px`,
              fontWeight: 600,
              // 新規取得は未取得の印、リユースはリユース候補の点と同じ色にして凡例と対応させる
              color: SOURCE_COLOR[line.kind] ?? "text.primary",
            }}
          >
            {line.text}
          </Typography>
        ))}
      </Box>
    );
  }
);
BulkRowSourceCell.displayName = "BulkRowSourceCell";
